import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { CopyButton } from "./CopyButton";
import { CopyField } from "./CopyField";
import { Icon } from "./icons";
import { Alert, Skeleton } from "./ui";
import { engineSpec } from "../lib/engines";
import { regionLabel } from "../lib/regions";
import type { Database, DatabaseConnection } from "../lib/types";

/**
 * How to reach a database.
 *
 * Laid out the way these panels are everywhere else in the category: the
 * string first, because it is the one thing almost everybody came for, then
 * the parts underneath for the cases where you need them individually.
 *
 * The string stays behind a reveal. It carries the password, so the request is
 * only made when asked for — but the row itself is always drawn, masked, so
 * revealing does not shift the rest of the card down the page.
 */
export function ConnectionDetails({
  database,
  connection,
  onNeedsConnection,
  loading,
  error,
  canReveal,
}: {
  database: Database;
  connection: DatabaseConnection | undefined;
  /** Called the first time any row is shown, so the parent fetches. */
  onNeedsConnection: () => void;
  loading: boolean;
  error: string | null;
  canReveal: boolean;
}) {
  const { t } = useTranslation("database");
  /*
   * Per row, not shared. Both strings come from one request, but revealing the
   * internal one should not also uncover the public one sitting beside it —
   * they are different secrets to be careless with, and one eye controlling
   * both meant showing either showed the other.
   */
  const [shown, setShown] = useState({ internal: false, external: false });
  const revealed = shown.internal || shown.external;

  function toggle(key: "internal" | "external") {
    const next = !shown[key];
    // Only ever asks for the data; the parent decides to keep it. Hiding again
    // re-masks what this session already has rather than dropping it.
    if (next) onNeedsConnection();
    setShown((current) => ({ ...current, [key]: next }));
  }
  const engine = engineSpec(database.engine);
  const string = connection?.connectionString ?? null;

  /*
   * Shown only once the database is published, and alongside the internal one
   * rather than instead of it: an attached app should keep going over the
   * internal network, which is private and unaffected when the port closes.
   */
  const exposed = database.externalPort !== null && database.externalHost !== null;
  const externalString = connection?.externalConnectionString ?? null;

  /*
   * Which rows exist is the engine's business, and it is two questions rather
   * than one. Redis has no database and no user. MongoDB has a user but no
   * named database — it creates one on first write. Drawing a row the engine
   * cannot fill puts an em dash where a value should be, which reads as
   * something having gone wrong with the database rather than as a property of
   * the engine.
   */
  const fields: { label: string; value: string | null }[] = [
    /*
     * The internal pair, and said so once there is an external one on screen
     * above it. Unqualified "Host" beside a published address is the row a
     * reader pastes into a GUI client from their laptop, where it resolves to
     * nothing -- the two differ in both halves, and only the label can say
     * which is which.
     */
    { label: exposed ? t("connection.internalHost") : t("connection.host"), value: database.internalHost },
    { label: exposed ? t("connection.internalPort") : t("connection.port"), value: String(database.port) },
    ...(engine.hasDatabaseName ? [{ label: t("connection.database"), value: database.databaseName }] : []),
    ...(engine.hasUser ? [{ label: t("connection.user"), value: database.username }] : []),
  ];

  /** Said only where it explains an absence somebody would otherwise chase. */
  const absence = !engine.hasUser
    ? t("connection.noUser", { engine: engine.label })
    : !engine.hasDatabaseName
      ? t("connection.noDatabaseName", { engine: engine.label })
      : null;

  /** One masked string, with its own reveal and its own copy. */
  function StringRow({
    value,
    name,
    visible,
    onToggle,
  }: {
    value: string | null;
    name: string;
    visible: boolean;
    onToggle: () => void;
  }) {
    return (
      <div className="flex items-center gap-2 border border-line bg-canvas px-3 py-2.5">
        {!canReveal ? (
          <span className="min-w-0 flex-1 font-mono text-xs text-fg-subtle">
            {t("connection.developersOnly")}
          </span>
        ) : loading ? (
          <Skeleton className="h-4 flex-1" />
        ) : visible && value ? (
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-fg">{value}</span>
        ) : (
          // Fixed-width mask rather than the real length: how long a secret
          // is is itself a small thing to leak, and a mask that changes width
          // when revealed makes the row jump.
          <span
            aria-hidden="true"
            className="min-w-0 flex-1 truncate font-mono text-xs text-fg-subtle"
          >
            ••••••••••••••••••••••••••••••••
          </span>
        )}

        {canReveal && (
          <>
            <button
              type="button"
              onClick={onToggle}
              disabled={loading}
              aria-label={visible ? t("connection.hide", { name }) : t("connection.reveal", { name })}
              className="shrink-0 text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg disabled:opacity-50"
            >
              <Icon name={visible ? "eye-off" : "eye"} className="size-4" />
            </button>
            {visible && value && <CopyButton value={value} label={t("connection.copy", { name })} />}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          {/* Named for where it works from: the host is a container name on
              one server's network, so only apps in this region resolve it. */}
          <p className="micro">
            {database.region
              ? t("connection.fromRegion", { region: regionLabel(database.region) })
              : exposed
                ? t("connection.fromYourApps")
                : t("connection.connectionString")}
          </p>
          <p className="text-xs text-fg-subtle">
            <Trans
              ns="database"
              i18nKey="connection.setAs"
              values={{ variable: engine.envKey }}
              components={{ key: <span className="font-mono text-fg-muted" /> }}
            />
          </p>
        </div>

        <StringRow
          value={string}
          name={t("connection.internal")}
          visible={shown.internal}
          onToggle={() => toggle("internal")}
        />

        {error && <Alert>{error}</Alert>}
        {revealed && !loading && !error && !string && (
          <Alert tone="warning">{t("connection.noAddress")}</Alert>
        )}
      </div>

      {/*
        Only once published. Same credential, different address — so it sits
        beside the internal string rather than replacing it, and the label says
        which is which instead of leaving two identical-looking rows.

        Both forms of that address live here: the string to paste into a
        client, and the bare host and port for a GUI that wants them in
        separate boxes. The bare pair used to sit two cards down, under "Who
        can reach it", which made the page answer "where do I reach it" in two
        places a screen apart — and left the panel about a *decision* carrying
        a field nobody goes there to copy.
      */}
      {exposed && (
        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <p className="micro">{t("connection.fromAnywhere")}</p>
            <p className="text-xs text-fg-subtle">{t("connection.openToInternet")}</p>
          </div>

          <StringRow
            value={externalString}
            name={t("connection.external")}
            visible={shown.external}
            onToggle={() => toggle("external")}
          />

          {database.externalHost && (
            <div className="space-y-1.5">
              <CopyField
                value={`${database.externalHost}:${database.externalPort}`}
                label={t("exposure.externalAddress")}
              />
              <p className="text-xs text-fg-subtle">{t("exposure.stableAddress")}</p>
            </div>
          )}
        </div>
      )}

      {/*
        The parts, each copyable on its own. Available without revealing
        anything: none of these is the password, and pasting a host into a
        migration tool should not require unmasking a secret first.
      */}
      <dl className="divide-y divide-line border border-line">
        {fields.map((field) => (
          <div key={field.label} className="flex items-center gap-3 px-3 py-2">
            <dt className="w-20 shrink-0 text-xs text-fg-subtle">{field.label}</dt>
            <dd className="min-w-0 flex-1 truncate font-mono text-xs text-fg">
              {field.value ?? <span className="text-fg-subtle">—</span>}
            </dd>
            {field.value && (
              <CopyButton value={field.value} label={t("connection.copy", { name: field.label.toLowerCase() })} />
            )}
          </div>
        ))}
      </dl>

      {absence && <p className="text-xs leading-5 text-fg-subtle">{absence}</p>}
    </div>
  );
}
